import React, { useEffect, useState } from "react";
import type { AffiliateClassesResponse } from "../../../interfaces/affiliateClasses";
import { useHttpClient } from "../../../hooks/useHttpClient";
import { useLocalization } from "../../../stores/localizationContext";
import { useUserContext } from "../../../stores/userContext";
import ClassCard from "./ClassCard/ClassCard";
import "./ManageClasses.scss";

const ManageClasses: React.FC = () => {
  const { user } = useUserContext();
  const api = useHttpClient();
  const { t } = useLocalization();

  const [data, setData] = useState<AffiliateClassesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchClasses = async () => {
      if (!user?.userId) return;

      setError(null);

      try {
        const endpoint = import.meta.env.VITE_GET_AFFILIATE_CLASSES.replace(
          "{educator_id}",
          user.userId
        );

        const response = await api.get(endpoint);
        setData(response.data);
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 403) {
          setError(t("unauthorized_access_error"));
        } else if (status === 404) {
          setError(t("educator_not_found", "Educator not found or not active."));
        } else {
          setError(t("generic_error", "Something went wrong. Please try again later."));
        }
      }
    };

    fetchClasses();
  }, [user?.userId]);

  if (error) {
    return (
      <div className="manage-classes__error">
        <p dir="auto">{error}</p>
      </div>
    );
  }

  if (data === null) {
    return null;
  }

  const classes = data.classes ?? [];

  return (
    <main className="manage-classes">
      <div className="container">
        <h1 className="manage-classes__title" dir="auto">
          {t("manage_my_classes_label")}
        </h1>

        {classes.length === 0 ? (
          <p className="manage-classes__empty" dir="auto">
            {t("no_classes_available", "No classes available.")}
          </p>
        ) : (
          <div className="manage-classes__grid">
            {classes.map((cls) => (
              <ClassCard key={cls.class_id} classData={cls} />
            ))}
          </div>
        )}
      </div>
    </main>
  );
};

export default ManageClasses;
